import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"

import { Evaluate, getAddress, isAddress, zeroAddress } from "viem"
import {
  type GetManagedVaultErrorType,
  type GetManagedVaultParameters,
  type GetManagedVaultReturnType,
  getManagedVault,
} from "../actions/getManagedVault"
import type {
  ExtendedConfig,
  ScopeKeyParameter,
  SimulationParameter,
} from "../types"
import { filterQueryOptions } from "../utils"

export type GetManagedVaultOptions = Evaluate<
  ExactPartial<GetManagedVaultParameters> &
    ScopeKeyParameter &
    SimulationParameter
>

export function getManagedVaultQueryKey(options: GetManagedVaultOptions = {}) {
  const { address, evcAddress, eulerEarnVaultLensAddress, ...parameters } =
    options
  return [
    "eVaultManaged",
    filterQueryOptions({
      address:
        address && isAddress(address) ? getAddress(address) : zeroAddress,
      evcAddress: evcAddress && getAddress(evcAddress),
      eulerEarnVaultLensAddress:
        eulerEarnVaultLensAddress && getAddress(eulerEarnVaultLensAddress),
      ...parameters,
    }),
  ] as const
}

export function getManagedVaultQueryFn(
  config: ExtendedConfig,
  options: GetManagedVaultOptions = {},
) {
  const { ...parameters } = options
  return getManagedVault(config, parameters)
}

export function getManagedVaultQueryOptions<config extends ExtendedConfig>(
  config: config,
  options: GetManagedVaultOptions,
) {
  return {
    gcTime: 0,
    queryFn: async ({ queryKey }) => {
      return getManagedVaultQueryFn(config, queryKey[1])
    },
    queryKey: getManagedVaultQueryKey(options),
  } satisfies QueryOptions<
    GetManagedVaultQueryFnData,
    GetManagedVaultErrorType,
    GetManagedVaultData,
    GetManagedVaultQueryKey
  >
}

export type GetManagedVaultQueryFnData = GetManagedVaultReturnType

export type GetManagedVaultData = GetManagedVaultQueryFnData

export type GetManagedVaultQueryKey = ReturnType<typeof getManagedVaultQueryKey>
